import { ApiError } from '@wellread/api';
import { clear, createStore } from 'idb-keyval';
import { keys, persister, queryClient } from './api';
import { authUnreachable, deleteAccount, signOut as endSession } from './auth';

/** Where a review's unsent answers are kept on this device (lib/review.ts). */
export const REVIEW_DRAFTS = 'wellread.review.drafts';

/** Drops everything this device cached: queries, the persisted copy, stored chapters, unsent answers. */
export async function clearDevice(): Promise<void> {
  queryClient.clear();
  try {
    localStorage.removeItem(REVIEW_DRAFTS);
  } catch {
    // Storage is refused here, so nothing was kept.
  }
  await persister.removeClient();
  await clear(createStore('wellread-chapters', 'chapters')).catch(() => undefined);
}

/** Signs out here and clears the books and notes cached on this device. */
export async function signOut(): Promise<void> {
  await endSession();
  await clearDevice();
}

/** Closes the account for good, then clears what this device kept of it. */
export async function closeAccount(): Promise<void> {
  await deleteAccount();
  await clearDevice();
}

const unauthorized = (e: unknown) => e instanceof ApiError && e.status === 401;

let ending = false;

/**
 * A 401 reached a read or a change. The client has already renewed the
 * session and asked again (createApi), so the API has turned the session
 * itself down: it's over on this device, which forgets the account's books
 * and notes, and the gate sends the reader to sign in. Not while the auth
 * server can't be reached, and once however many requests fail together.
 */
async function rejected(): Promise<void> {
  if (ending || authUnreachable()) return;
  ending = true;
  try {
    await endSession({ local: true });
    await clearDevice();
  } finally {
    ending = false;
  }
}

const unpaid = (e: unknown) => e instanceof ApiError && e.status === 402;

/**
 * A 402 reached a read or a change: the reader's free days or paid month
 * ran out while the app was open (or they have no credits left, which says
 * the same of the copy held here). The reader is asked for again, and the
 * gate shows what the answer says.
 */
function lapsed(): void {
  void queryClient.invalidateQueries({ queryKey: keys.me });
}

/** Listens for a 401 or a 402 on every read and change. Call once, at start. */
export function watchSession(): void {
  queryClient.getQueryCache().subscribe((e) => {
    if (e.type !== 'updated' || e.action.type !== 'error') return;
    if (unauthorized(e.action.error)) void rejected();
    else if (unpaid(e.action.error)) lapsed();
  });
  queryClient.getMutationCache().subscribe((e) => {
    if (e.type !== 'updated' || e.action.type !== 'error') return;
    if (unauthorized(e.action.error)) void rejected();
    else if (unpaid(e.action.error)) lapsed();
  });
}
